/* eslint-disable no-useless-escape */
/**
 * Utility file to support the FormattedMarkdown component.
 * This logic has been pulled out for testing purposes.
 *
 * This file depends on styles in
 * stylesheets/shared-package/default.css and
 * stylesheets/discussion-package/discussion.less
 */
// TODO LP-4439(diedra): Switch over to using a real formatting tools like
// react-markdown to clean up the way we're doing this formatting, improving the
// formatting in general, and making a better formatting user experience.
import "../../../stylesheets/video-package/youtube-links.less";

import {t} from "@lingui/core/macro";
import * as React from "react";

import {staticUrl} from "@khan/urls";

// Used for finding a timestamp formatted as TIME:TIME in a post.
const timestampRegex = /(\d{1,3}):([0-5]\d)/g;
// Finds the timestamp in a post and returns the total length in seconds.
export const getTimestampSecondsFromText = (text: string): number => {
    let minutes = 0;
    let seconds = 20;

    const timestampMatch = timestampRegex.exec(text);
    if (timestampMatch) {
        minutes = parseFloat(timestampMatch[1]);
        seconds = parseFloat(timestampMatch[2]);
    }

    return minutes * 60 + seconds;
};
// Turns a number of seconds into the minutes and remaining seconds that that number
// of seconds can be broken into.
export const getTimestampFromSeconds = (
    totalSeconds: number,
): {
    minutes: string;
    seconds: string;
} => {
    const rawMinutes = Math.floor(totalSeconds / 60);
    const rawSeconds = totalSeconds - rawMinutes * 60;

    const minutes = rawMinutes.toString();
    const secondsString = rawSeconds.toString();
    const seconds =
        secondsString.length > 1 ? secondsString : 0 + secondsString;

    return {minutes, seconds};
};

const escape = {
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#x27;",
    "`": "&#x60;",
    "=": "&#x3D;",
} as const;
const escapeCharacter = (character: string) => escape[character];

const possible = /[&<>"'`=]/;
const badChars = /[&<>"'`=]/g;

const escapeExpression = (string: string) => {
    if (typeof string !== "string") {
        // Don't escape SafeStrings, since they're already safe.
        // @ts-expect-error - TS2339 - Property 'toHTML' does not exist on type 'never'.
        if (string && string.toHTML) {
            // @ts-expect-error - TS2339 - Property 'toHTML' does not exist on type 'never'.
            return string.toHTML();
        }
        if (string == null) {
            return "";
        }
        if (!string) {
            return string + "";
        }

        // Force a string conversion as this will be done by the append regardless and
        // the regex test will do this transparently behind the scenes, causing issues if
        // an object's to string has escaped characters in it.
        string = "" + string;
    }

    if (!possible.test(string)) {
        return string;
    }
    return string.replace(badChars, escapeCharacter);
};

const autolink = (text: string, nofollow?: boolean | null) => {
    // From http://daringfireball.net/2010/07/improved_regex_for_matching_urls
    const regex =
        /\b(?:(?:https?:\/\/|www\d{0,3}[.]|[a-z0-9.\-]+[.][a-z]{2,4}\/)(?:[^\s()<>&]+|&amp;|\((?:[^\s()<>]|(?:\([^\s()<>]+\)))*\))+(?:\((?:[^\s()<>]|(?:\([^\s()<>]+\)))*\)|[^\s`!()\[\]{};:'".,<>?«»“”‘’&]))/gi;

    return text.replace(regex, (url: string) => {
        // Check if the URL is missing the protocol in front
        if (!/^https?:\/\//.test(url)) {
            // Add the HTTPS protocol if it's a KA URL
            if (url.match(/[^\/]*khanacademy.org|^\//)) {
                url = "https://" + url;
            } else {
                // Don't link protocol-less URLs that aren't KA URLs
                return url;
            }
        }

        const nofollowAttr = nofollow ? 'rel="nofollow"' : "";

        return `<a  href="${url}" ${nofollowAttr}>${url}</a>`;
    });
};

const getVideoTimestampLinkString = (
    // String of timestamp with minutes separated from seconds by a colon.
    // Ex: 2:55
    timestamp: string,
    // Minute part of timestamp. Ex: 2
    minutes: string,
    // Second part of timestamp. Ex: 55
    seconds: string,
    children?: string,
) => {
    /* TODO(diedra): Use new wonderblocks contentVideo medium icon. */
    const playIcon = `<img src=${staticUrl(
        "/images/play.png",
    )} class='youTubePlayIcon' aria-hidden="true" alt="" />`;

    const numSeconds = 60 * Number(minutes) + Number(seconds);

    const timestampExplanation = t`Click to jump to ${timestamp} in video`;
    const timestampAria = `${timestamp} ${timestampExplanation}`;

    const linkContent = children ? children : `${playIcon} ${timestamp}`;

    // The onClick handler for these elements are added in the linkTimestampsToModel method
    // in apps/khanacademy/src/video-package/util.js
    return `<div class='youTubeLink' data-seconds='${numSeconds}' tabindex='0' role='link' aria-label='${timestampAria}'>${linkContent}</div>`;
};

const formatTimestamp_ = (
    timestamp: string,
    minutes: string,
    seconds: string,
) => {
    return getVideoTimestampLinkString(timestamp, minutes, seconds);
};

type VideoTimestampLinkProps = {
    // String of timestamp with minutes separated from seconds by a colon.
    // Ex: 2:55
    timestamp: string;
    // Minute part of timestamp. Ex: 2
    minutes: string;
    // Second part of timestamp. Ex: 55
    seconds: string;
    children?: any;
};

export const VideoTimestampLink = (
    props: VideoTimestampLinkProps,
): React.ReactElement<React.ComponentProps<"span">> => ( // HACK(diedra): We don't want to inject html into an element like this,
    // but that's how our methods in this file for formatting content work atm.
    // Someone please think of the children and implement react-markdown or
    // something.
    <span
        dangerouslySetInnerHTML={{
            __html: getVideoTimestampLinkString(
                props.timestamp,
                props.minutes,
                props.seconds,
                props.children,
            ),
        }}
    />
);

/* Format the content using our own flavor of markdown used in discussions. Use
 * nofollow to add rel=nofollow to any links included in the content (to avoid
 * giving them link juice).
 *
 * Example:
 *
 *     > formatContent("My *spammy* blog: http://spam.com")
 *     'My <strong>spammy</strong> blog: <a  href="http://spam.com">http://spam.com</a>'
 *
 *     > formatContent("My *spammy* blog: http://spam.com", true)
 *     'My <strong>spammy</strong> blog: <a rel="nofollow"
 *     href="http://spam.com">http://spam.com</a>'
 */
export const formatContent = (
    string: string,
    nofollow?: boolean | null,
): string => {
    // Escape user generated content.
    let content = escapeExpression(string);

    const newlineRegex = /[\n]/g;
    content = content.replace(newlineRegex, "<br>");

    // Replace Markdown with HTML tags. (Keep in sync with edit function in
    // discussion.js.)
    // TODO(diedra): Remove above comment if/when discussion.js is sleepin' with
    // the fishies.
    content = content.replace(
        /(\W|^)_(\S.*?\S)_(\W|$)/g,
        function (m, a, b, c) {
            return a + "<em>" + b + "</em>" + c;
        },
    );

    content = content.replace(
        /(\W|^)\*(\b.*?\b)\*(\W|$)/g,
        function (m, a, b, c) {
            return a + "<strong>" + b + "</strong>" + c;
        },
    );

    // Difference between <code> and <pre><code>: http://stackoverflow.com/q/4611591/
    content = content.replace(
        /&#x60;&#x60;&#x60;(.*?)&#x60;&#x60;&#x60;/gm,
        function (m, n) {
            // Remove leading line breaks.
            n = n.replace(/^\s*(<br>)+/, "");
            // Remove trailing line breaks.
            n = n.replace(/(<br>)+\s*$/, "");
            return (
                "<pre><code class='discussion-code-format discussion-code-block'>" +
                n +
                "</code></pre>"
            );
        },
    );

    content = content.replace(/&#x60;(.*?)&#x60;/g, function (m, n) {
        return (
            "<code class='discussion-code-format discussion-code-inline'>" +
            n +
            "</code>"
        );
    });

    content = autolink(content, nofollow);

    // Format video timestamps.
    // This needs to happen after the `autolink` call to avoid formatting the image
    // url we use for the icon.
    content = content.replace(timestampRegex, formatTimestamp_);

    return content;
};
